// @arch:pure-engine
// taskDeadline.js — RCA lentidão fix H: teto wall-clock ABSOLUTO por TASK do DAG.
//
// O #603 (fix A daquele RCA) garantiu que uma CHAMADA de LLM termina (idle 120s +
// teto 10min por chamada). Mas ninguém garantia que a TASK termina: 40 iterações ×
// (32s..10min) = 21min..horas, tudo dentro das regras — foi exatamente o "40+ min
// na primeira task" medido em prod (turn_start 02:46:59 sem NENHUM evento por 76min).
//
// Este wrapper dá à task um deadline próprio: estourou ⇒ aborta o loop DA TASK
// (AbortController por task, encadeado ao Stop do usuário) e devolve o outcome
// HONESTO { blocked: true, blockedReason: 'task_timeout' } — o mesmo contrato WSX
// do executor (recuperável; NÃO envenena o learning como vermelho de oracle).
//
// Puro e injetável (timers por parâmetro nos testes); zero mudança no motor do loop.

import { ABORT_REASON, abortReasonOf, abortWithReason } from './abortReason.js';

export const DAG_TASK_DEADLINE_MS = 15 * 60 * 1000; // 15min — folga pra task legítima, corta a fugitiva

/**
 * Envolve um runTask(task, runCtx) com um deadline absoluto por task.
 * - injeta `runCtx.signal` (controller POR TASK, encadeado ao parentSignal);
 * - deadline estourado ⇒ aborta o loop da task e devolve blocked/task_timeout;
 * - Stop do usuário (parentSignal) ⇒ pass-through (semântica 'aborted' de sempre);
 * - ms ≤ 0 ⇒ teto desligado (retorna o runTask original, byte-idêntico).
 *
 * @param {(task:object, runCtx?:object)=>Promise<any>} runTask
 * @param {{ ms?: number, parentSignal?: AbortSignal, onTimeout?: (task:object)=>void,
 *           setTimeoutFn?: typeof setTimeout, clearTimeoutFn?: typeof clearTimeout }} [opts]
 * @returns {(task:object, runCtx?:object)=>Promise<any>}
 */
export function withTaskDeadline(runTask, {
  ms = DAG_TASK_DEADLINE_MS,
  parentSignal,
  onTimeout,
  setTimeoutFn = setTimeout,
  clearTimeoutFn = clearTimeout,
} = {}) {
  if (!(Number(ms) > 0)) return runTask;
  return async (task, runCtx = {}) => {
    const ctl = new AbortController();
    const onParentAbort = () => abortWithReason(ctl, abortReasonOf(parentSignal));
    if (parentSignal) {
      if (parentSignal.aborted) onParentAbort();
      else parentSignal.addEventListener('abort', onParentAbort, { once: true });
    }
    let timedOut = false;
    const timer = setTimeoutFn(() => {
      timedOut = true;
      try { onTimeout?.(task); } catch { /* aviso é best-effort */ }
      abortWithReason(ctl, ABORT_REASON.TASK_TIMEOUT); // o loop cai no caminho 'aborted' e o wrapper reclassifica
    }, ms);
    try {
      const r = await runTask(task, { ...runCtx, signal: ctl.signal });
      // Deadline venceu a corrida ⇒ outcome HONESTO independe do que o loop devolveu
      // (o abort interno vira 'aborted'; aqui a verdade é task_timeout).
      return timedOut
        ? { ...(r && typeof r === 'object' ? r : {}), ok: false, blocked: true, blockedReason: 'task_timeout', stopReason: 'task_timeout' }
        : r;
    } catch (e) {
      if (timedOut) {
        return { ok: false, blocked: true, blockedReason: 'task_timeout', stopReason: 'task_timeout', error: e?.message || String(e) };
      }
      throw e; // erro real sem deadline: contrato de sempre (o executor trata)
    } finally {
      clearTimeoutFn(timer);
      parentSignal?.removeEventListener?.('abort', onParentAbort);
    }
  };
}
